/* eslint-disable @typescript-eslint/naming-convention */
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  TemplateRef,
  viewChild
} from '@angular/core';

import { UntypedFormControl } from '@angular/forms';

import { Subject } from 'rxjs';
import { finalize, startWith, takeUntil } from 'rxjs/operators';
import { InsuranceAddComponent } from '@sondermind/features/account-edit';
import { FEATURE_FLAG_SERVICE, IFeatureFlagService, FeatureFlagKey } from '@sondermind/data-access/feature-flags';
import { ClientUser } from '@sondermind/utilities/models-user-client';
import { RadioOption } from '@sondermind/iris/radio-group';
import { IrisButtonContainerAlignments, IrisButtonTypes } from '@sondermind/iris/button';
import { IrisButtonTypeAttributeOptions } from '@sondermind/iris/shared';
import { ICostEstimateContextAttributes } from '@sondermind/utilities/models-client-insurance';

export interface IInsuranceInfoSubmitted {
  reviewInsurance: boolean;
  verificationOutage?: boolean;
  exitSetup?: boolean;
  selfPaySelected?: boolean;
  payorDowntimeAccepted?: boolean;
  bvErrorEncountered?: boolean;
}
@Component({
  selector: 'client-setup-insurance-info',
  templateUrl: './setup-insurance-info.component.html',
  styleUrls: ['./setup-insurance-info.component.scss'],
  standalone: false
})
export class SetupInsuranceInfoComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() useInSessionRequest: boolean = false;
  @Input() isMobile: boolean = false;
  @Input() isProposeATime: boolean = false;
  @Input() user!: ClientUser;
  @Input() costEstimateContext!: ICostEstimateContextAttributes;
  @Input() fromDirectBooking: boolean = false;
  // emit true if BV error, false if not
  @Output() insuranceInfoSubmitted: EventEmitter<IInsuranceInfoSubmitted> = new EventEmitter<IInsuranceInfoSubmitted>();

  @ViewChild(InsuranceAddComponent) insuranceFormComponent!: InsuranceAddComponent;

  // Template for the action button that parent component can render
  readonly actionButtonTemplate = viewChild.required<TemplateRef<unknown>>('actionButton');

  addingInsuranceControl = new UntypedFormControl(false);
  chcHasAnOutageFeatureFlag: boolean = false;
  submitting: boolean = false;
  showInsuranceHelperText: boolean = false;
  hideContent: boolean = false; // Controlled by insurance-add component
  yesOrNo: Array<RadioOption<boolean>> = [
    { name: `Health insurance`, value: true },
    { name: 'Self-pay, without insurance', value: false }
  ];
  private destroyed$ = new Subject<void>();

  IrisButtonContainerAlignments = IrisButtonContainerAlignments;
  IrisButtonTypes = IrisButtonTypes;
  IrisButtonTypeAttributeOptions = IrisButtonTypeAttributeOptions;
  constructor(
    @Inject(FEATURE_FLAG_SERVICE)
    private featureFlagService: IFeatureFlagService
  ) { }

  ngOnInit(): void {
    this.setInsurance();

    this.featureFlagService.isEnabled$(FeatureFlagKey.CHC_OUTAGE)
      .subscribe((CHC_OUTAGE) => {
        this.chcHasAnOutageFeatureFlag = CHC_OUTAGE;
      });
  }

  ngAfterViewInit(): void {
    // do this here rather than ngOnInit to allow form to load
    this.addingInsuranceControl.valueChanges.pipe(
      startWith(this.addingInsuranceControl.value),
      takeUntil(this.destroyed$)
    ).subscribe(() => {
      this.updateSelfPay();
    });

    if (this.costEstimateContext) {
      this.insuranceFormComponent.insuranceForm.form.patchValue({
        ...this.costEstimateContext
      });
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onHideParentContent(hide: boolean): void {
    this.hideContent = hide;

    if (!hide) {
      this.stopSubmitting();
    }
  }

  onInsuranceAddClosed(event: any): void {
    this.stopSubmitting();

    if (event.selfPayAfterBvError && event.selfPaySelected) {
      // Self-pay was chosen from a BV error modal
      this.insuranceInfoSubmitted.emit({ reviewInsurance: false, selfPaySelected: true, bvErrorEncountered: true });
    } else if (event.selfPaySelected) {
      // Self-pay was already handled by the child component, just emit the result
      // Don't set addingInsuranceControl.setValue(false) here as it causes template flash
      // The component will be navigated away from anyway

      // Immediately hide content to prevent any flash during transition
      this.hideContent = true;

      this.insuranceInfoSubmitted.emit({ reviewInsurance: false, selfPaySelected: true });
    } else if (event.payorDowntimeAccepted) {
      this.insuranceInfoSubmitted.emit({ reviewInsurance: false, payorDowntimeAccepted: true });
    } else if (event.medicaidPlanRejected) {
      this.insuranceInfoSubmitted.emit({ reviewInsurance: false, exitSetup: true });
    } else if (event.suggestedPayerRejected) {
      // Suggested payer was rejected - navigate to insurance review screen
      this.insuranceInfoSubmitted.emit({ reviewInsurance: true });
    } else if (event.multiplePncSecondAttempt) {
      // Second multiple PNC attempt - navigate to insurance review screen
      this.insuranceInfoSubmitted.emit({ reviewInsurance: true });
    } else if (event.regularBvSecondAttempt) {
      // Regular second BV failure - navigate to insurance review screen
      this.insuranceInfoSubmitted.emit({ reviewInsurance: true });
    } else if (event.suggestedPayerBvFailure) {
      // BV failure after accepting suggested payer - navigate to insurance review screen
      this.insuranceInfoSubmitted.emit({ reviewInsurance: true });
    } else if (event.noCoverageAccepted) {
      // User accepted to continue with no coverage insurance - navigate to insurance review screen
      this.insuranceInfoSubmitted.emit({ reviewInsurance: true });
    } else if (event.bvError) {
      // The child component will show the BV error banner
      this.insuranceInfoSubmitted.emit({ reviewInsurance: false, bvErrorEncountered: true });
    } else if (event.insuranceUpdated) {
      // Insurance was actually updated successfully
      this.insuranceInfoSubmitted.emit({ reviewInsurance: false });
    } else {
      // Do nothing
    }
    // For first BV failure don't emit anything - just stay on current page
  }

  /**
   * Check if the new user can click the next button based on what forms are available
   * and their status. If a form is active but not able to submit, do not allow next.
   */
  get allowNext(): boolean {
    if (Boolean(this.addingInsuranceControl.value) && !this.canSubmitInsurance) { return false }

    return true;
  }

  get canSubmitInsurance(): boolean {
    return this.insuranceFormComponent && !this.insuranceFormComponent.insuranceForm.invalid;
  }

  /**
   * Toggle self_pay on the child InsuranceFormComponent depending on what the
   * user selects in the UI for adding insurance.
   */
  updateSelfPay(): void {
    this.insuranceFormComponent.insuranceForm.form.patchValue({
      // eslint-disable-next-line no-extra-boolean-cast
      self_pay: !Boolean(this.addingInsuranceControl.value)
    });
  }

  newClientSubmit(): void {
    this.startSubmitting();

    if (!this.addingInsuranceControl.value) {
      this.insuranceFormComponent.insuranceForm.submit().pipe(
        finalize(() => {
          this.stopSubmitting();
        })
      ).subscribe({
        next: () => {
          this.insuranceInfoSubmitted.emit({ reviewInsurance: false, selfPaySelected: true });
        },
        error: (err) => {
          // eslint-disable-next-line no-console
          console.error(err);
        }
      });
    } else {
      this.insuranceFormComponent.submit();
    }
  }

  /**
   * Wrapper for better unit tests.
   */
  startSubmitting(): void {
    this.submitting = true;
  }

  /**
   * Wrapper for better unit tests.
   */
  stopSubmitting(): void {
    this.submitting = false;
  }

  /**
   * Default EAP and Insurance selections based on user preference.
   * Only show helper text if use indicated they wanted to add insurance on their NCR.
   */
  setInsurance(): void {
    this.addingInsuranceControl.setValue(!this.user?.hasCashPref);
    this.showInsuranceHelperText = !this.user?.hasCashPref;
  }
}
